import type { AiTokenUsage } from "./aiTokenUsage";
import type { PersonaCategoryForMatching } from "./atmospherePersonaCategoryPrompt";
import type { GlobalAssignmentBlockDiagnostics } from "./scriptRoleGlobalAssignmentDiagnostics";

export interface ScriptRoleMatchFailure {
  tempRoleId: string;
  error: string;
  /** 自动匹配无解，保留给审核阶段人工选择。 */
  skipped?: boolean;
  blockDiagnostics?: GlobalAssignmentBlockDiagnostics;
}
import type { RankedRoleCandidate, RoleAssignmentItem, ScriptRolePersona } from "./scriptRoleAssignment";
import type { ParsedScriptSheetRow } from "./feishuScriptSheet";

export type ScriptAssignmentMatchJobStatus =
  | "pending"
  | "scoring"
  | "selecting"
  | "completed"
  | "failed";

export const ACTIVE_SCRIPT_ROLE_MATCH_JOB_STATUSES: ScriptAssignmentMatchJobStatus[] = [
  "pending",
  "scoring",
  "selecting",
];

export function isActiveScriptRoleMatchJob(
  job: ScriptAssignmentMatchJob | null | undefined
): job is ScriptAssignmentMatchJob {
  return !!job && ACTIVE_SCRIPT_ROLE_MATCH_JOB_STATUSES.includes(job.status);
}

/** 失败或部分失败后重试时，应保留 matchJob / matchRankings 续跑 */
export function isResumableScriptRoleMatchJob(
  job: ScriptAssignmentMatchJob | null | undefined
): job is ScriptAssignmentMatchJob {
  if (!job) return false;
  if (ACTIVE_SCRIPT_ROLE_MATCH_JOB_STATUSES.includes(job.status)) return true;
  if (job.status === "failed") return true;
  if ((job.failures?.length ?? 0) > 0) return true;
  return false;
}

export function pickMatchRankingsForRoles(
  matchRankings: Record<string, RankedRoleCandidate[]> | undefined,
  tempRoleIds: Iterable<string>
): Record<string, RankedRoleCandidate[]> {
  const allowed = new Set(tempRoleIds);
  const picked: Record<string, RankedRoleCandidate[]> = {};
  for (const [tempRoleId, ranked] of Object.entries(matchRankings ?? {})) {
    if (allowed.has(tempRoleId)) {
      picked[tempRoleId] = ranked;
    }
  }
  return picked;
}

export function upsertScriptRoleMatchFailure(
  failures: ScriptRoleMatchFailure[],
  failure: ScriptRoleMatchFailure
) {
  const byRoleId = new Map(failures.map((item) => [item.tempRoleId, item]));
  byRoleId.set(failure.tempRoleId, failure);
  return [...byRoleId.values()];
}

export function buildResumedMatchJobForRetry(input: {
  previous: ScriptAssignmentMatchJob;
  rolesToMatchCount: number;
  rankedRoleCount: number;
}): ScriptAssignmentMatchJob {
  const scoredCount = Math.min(input.rankedRoleCount, input.rolesToMatchCount);
  const allScored =
    input.rankedRoleCount >= input.rolesToMatchCount && input.rolesToMatchCount > 0;
  const status: ScriptAssignmentMatchJobStatus = allScored ? "selecting" : "scoring";

  return {
    ...input.previous,
    status,
    totalRoles: input.rolesToMatchCount,
    scoringDone: allScored ? input.rolesToMatchCount : scoredCount,
    // rolesToMatchCount 只包含尚未成功分配的角色，选择进度和失败必须按本轮重算。
    selectionDone: 0,
    failures: [],
    error: undefined,
    completedAt: undefined,
    lastHeartbeatAt: new Date().toISOString(),
  };
}

export interface ScriptAssignmentMatchProgressAssignment {
  tempRoleId: string;
  wxid: string;
  number: string;
  nickname: string;
  score: number;
  reason: string;
  selectionSource?: import("./scriptRoleAssignment").ScriptRoleSelectionSource;
}

/** 轮询匹配进度用的轻量快照，不含画像等大字段。 */
export interface ScriptAssignmentMatchProgressSnapshot {
  found: boolean;
  fingerprintMatched: boolean;
  updatedAt: string | null;
  memberCount: number;
  tempRoleCount: number;
  assignmentCount: number;
  matchJob: ScriptAssignmentMatchJob | null;
  assignments: ScriptAssignmentMatchProgressAssignment[];
}

export interface ScriptAssignmentMatchJob {
  status: ScriptAssignmentMatchJobStatus;
  totalRoles: number;
  scoringDone: number;
  selectionDone: number;
  failures: ScriptRoleMatchFailure[];
  matchTokenUsage?: AiTokenUsage;
  startedAt: string;
  completedAt?: string;
  error?: string;
  /** 最近一次打分或选择阶段处理到的临时角色 */
  lastActivityTempRoleId?: string;
  /** 服务端心跳时间，用于区分「进行中」与「真卡住」 */
  lastHeartbeatAt?: string;
  globalAssignmentStats?: {
    algorithm: "beam";
    beamWidth: number;
    attempts: number;
    expandedRoleCount: number;
    exploredStates: number;
    durationMs: number;
    expandedSelectionCount: number;
    contradictionAttempts?: number;
  };
}

/** 前端展示用默认值；服务端以 SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY 环境变量为准 */
export const SCRIPT_ROLE_ASSIGNMENT_CONCURRENCY = 20;
/** 单次 LLM 结构化输出超时（毫秒），防止请求无限挂起 */
export const SCRIPT_ROLE_LLM_TIMEOUT_MS = 3 * 60 * 1000;
/** Mongo matchJob 超过该时间无心跳则视为僵死，允许重新入队 */
export const SCRIPT_ROLE_MATCH_JOB_STALE_MS = 3 * 60 * 1000;

export function isScriptRoleMatchJobStale(job: ScriptAssignmentMatchJob): boolean {
  const anchor = job.lastHeartbeatAt ?? job.startedAt;
  if (!anchor) return true;
  const at = Date.parse(anchor);
  if (!Number.isFinite(at)) return true;
  return Date.now() - at > SCRIPT_ROLE_MATCH_JOB_STALE_MS;
}
export const SCRIPT_ROLE_ASSIGNMENT_PIPELINE_VERSION = "2026-07-11-v5-review-audit";

export const SCRIPT_ROLE_MATCH_EXECUTION_SCHEMA_VERSION = "2026-07-11-v1";

export interface ScriptRoleMatchMemberSnapshot {
  wxid: string;
  nickname: string;
  avatar: string;
  group: string;
  category: string;
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  number: string;
  deviceName: string;
  notes: string;
  specificHealthTags: string[];
  specificProductTags: string[];
  specificFamilyTags: string[];
  specificStoryTags: string[];
  specificToneTags: string[];
  forbiddenTopics: string[];
  personaSummary: string;
  compactProfile: string;
  profileExtractedAt: string | null;
}

export interface ScriptRoleMatchImageUnderstandingSnapshot {
  content: string;
  modelId: string;
  providerId: string;
  remoteId: string;
  understoodAt: string;
  contentSha256?: string;
}

export interface ScriptRoleMatchLlmExecutionSnapshot {
  executionId: string;
  scope: string;
  tempRoleIds: string[];
  modelId: string;
  providerId: string;
  remoteId: string;
  completedAt: string;
}

export type ScriptRolePersonaExecutionSnapshot =
  | {
      tempRoleId: string;
      generator: "inline-v1";
      persona: ScriptRolePersona;
      completedAt: string;
    }
  | {
      tempRoleId: string;
      generator: "llm";
      persona: ScriptRolePersona;
      modelId: string;
      providerId: string;
      remoteId: string;
      completedAt: string;
    };

/** 服务端在实际匹配 worker 内冻结；客户端不能创建或覆盖。 */
export interface ScriptRoleMatchExecutionSnapshot {
  schemaVersion: typeof SCRIPT_ROLE_MATCH_EXECUTION_SCHEMA_VERSION;
  startedAt: string;
  memberSnapshots: ScriptRoleMatchMemberSnapshot[];
  personaCategories: PersonaCategoryForMatching[];
  imageTextsByFileToken: Record<string, string>;
  imageUnderstandingByFileToken: Record<
    string,
    ScriptRoleMatchImageUnderstandingSnapshot
  >;
  personaExecutions: Record<string, ScriptRolePersonaExecutionSnapshot>;
  llmExecutions: ScriptRoleMatchLlmExecutionSnapshot[];
}

export interface ScriptAssignmentTempRole {
  tempRoleId: string;
  speakerLabel: string;
  utteranceCount: number;
  contentBeat: import("./scriptRoleDiversity").ScriptContentBeatType;
  firstRowIndex: number;
  lastRowIndex?: number;
  contentRowNumbers?: number[];
}

export interface ScriptAssignmentSessionCache {
  spreadsheetToken: string;
  sheetId: string;
  group: string;
  /** 当前剧本内容 + 匹配管线版本的指纹，防止同一工作表复用旧剧本缓存。 */
  sourceFingerprint: string;
  imageTextsByFileToken: Record<string, string>;
  tempRoles: ScriptAssignmentTempRole[];
  memberCount: number;
  personas: Record<string, ScriptRolePersona>;
  /** 服务端生成画像时保存的权威生成器/模型快照。 */
  personaExecutions?: Record<string, ScriptRolePersonaExecutionSnapshot>;
  assignments: RoleAssignmentItem[];
  /** 打分阶段 TopN 候选，用于服务重启后续跑 */
  matchRankings?: Record<string, RankedRoleCandidate[]>;
  matchJob?: ScriptAssignmentMatchJob;
  /** 实际匹配时的服务端权威输入与模型调用快照。 */
  matchExecution?: ScriptRoleMatchExecutionSnapshot;
  updatedAt: string;
}

export function areScriptRolePersonasEqual(
  left: ScriptRolePersona | undefined,
  right: ScriptRolePersona | undefined
) {
  if (!left || !right) return false;
  return (
    left.tempRoleId === right.tempRoleId &&
    left.personaSummary === right.personaSummary &&
    JSON.stringify(left.toneTags) === JSON.stringify(right.toneTags) &&
    JSON.stringify(left.topicTags) === JSON.stringify(right.topicTags) &&
    JSON.stringify(left.sampleQuotes) === JSON.stringify(right.sampleQuotes)
  );
}

const STORAGE_PREFIX = "script-role-assignment:";

function hashFingerprintSource(source: string) {
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;

  for (let index = 0; index < source.length; index += 1) {
    const code = source.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
  }

  return [left, right]
    .map((value) => (value >>> 0).toString(16).padStart(8, "0"))
    .join("");
}

/**
 * 只纳入会影响角色识别、上下文和分配资格的字段。
 * D 列也必须参与：已有编号会让对应角色块跳过匹配。
 */
export function buildScriptAssignmentSourceFingerprint(
  rows: ParsedScriptSheetRow[],
  overwriteMode = false
) {
  const source = JSON.stringify({
    pipelineVersion: SCRIPT_ROLE_ASSIGNMENT_PIPELINE_VERSION,
    overwriteMode,
    rows: rows.map((row) => [
      row.rowIndex,
      row.role,
      row.roleIsInherited,
      row.roleImageFileToken ?? "",
      row.cellImages.B ?? "",
      row.cells.C ?? "",
      row.cells.D ?? "",
      row.cellImages.C ?? "",
      row.cellImages.D ?? "",
    ]),
  });
  return `${SCRIPT_ROLE_ASSIGNMENT_PIPELINE_VERSION}:${hashFingerprintSource(source)}`;
}

export function buildScriptAssignmentCacheKey(input: {
  spreadsheetToken: string;
  sheetId: string;
  group: string;
}) {
  return `${STORAGE_PREFIX}${input.spreadsheetToken}:${input.sheetId}:${input.group}`;
}

export function buildScriptAssignmentCacheLookup(input: {
  spreadsheetToken: string;
  sheetId: string;
  group: string;
  parsedRows: ParsedScriptSheetRow[];
  overwriteMode?: boolean;
}): ScriptRoleAssignmentCacheKey {
  return {
    spreadsheetToken: input.spreadsheetToken,
    sheetId: input.sheetId,
    group: input.group.trim(),
    sourceFingerprint: buildScriptAssignmentSourceFingerprint(
      input.parsedRows,
      input.overwriteMode === true
    ),
  };
}

export interface ScriptRoleAssignmentCacheKey {
  spreadsheetToken: string;
  sheetId: string;
  group: string;
  sourceFingerprint: string;
}

export function validateScriptAssignmentCacheIntegrity(
  cache: Pick<ScriptAssignmentSessionCache, "tempRoles" | "personas" | "assignments">
) {
  const tempRoleIds = cache.tempRoles.map((item) => item.tempRoleId);
  const allowedIds = new Set(tempRoleIds);
  if (allowedIds.size !== tempRoleIds.length) {
    throw new Error("分配缓存包含重复临时角色");
  }

  const unknownPersonaIds = Object.keys(cache.personas).filter((id) => !allowedIds.has(id));
  if (unknownPersonaIds.length > 0) {
    throw new Error(`分配缓存包含 ${unknownPersonaIds.length} 个不属于当前剧本的画像`);
  }

  const assignmentIds = cache.assignments.map((item) => item.tempRoleId);
  const uniqueAssignmentIds = new Set(assignmentIds);
  if (uniqueAssignmentIds.size !== assignmentIds.length) {
    throw new Error("分配缓存包含重复匹配结果");
  }

  const unknownAssignmentIds = assignmentIds.filter((id) => !allowedIds.has(id));
  if (unknownAssignmentIds.length > 0) {
    throw new Error(`分配缓存包含 ${unknownAssignmentIds.length} 个不属于当前剧本的匹配结果`);
  }
}
